// Renders a Lint targeted-replacement patch (anchor + before + after)
// against the page's current text as a sequence of typed segments the
// SPA can paint inline. No diff library — the patch shape already tells
// us exactly what to highlight: locate `before` in the page, mark it as
// removed, mark `after` as added, surround with N context lines.
//
// Pure function; no Vue / reactivity / fetches. Lives in composables/
// rather than utils/ because it pairs with `LintNoteCard.vue` and the
// repo's convention puts UI-supporting logic alongside other composables
// even when the function isn't a `use*()` reactive helper.
//
// Returns an array of segments rendered top-to-bottom:
//   - { kind: 'context', text }  — surrounding lines unchanged
//   - { kind: 'removed', text }  — the patch's `before` block
//   - { kind: 'added',   text }  — the patch's `after` block
//
// Empty `after` collapses to a no-op `added` segment (useful: the SPA
// can still render a "this block will be deleted" hint without the
// caller branching).
//
// When `before` is not in the page (drifted since the patch was
// proposed), returns a single `{ kind: 'orphan', current, patch }`
// segment so the caller can show "page changed since this patch was
// generated — Edit to refresh."

export interface LintPatch {
  anchor: string
  before: string
  after: string
}

export type DiffSegment =
  | { kind: 'context'; text: string }
  | { kind: 'removed'; text: string }
  | { kind: 'added'; text: string }
  | { kind: 'orphan'; current: string; patch: LintPatch }

const DEFAULT_CONTEXT_LINES = 3

export interface DiffRenderOptions {
  contextLines?: number
}

export function renderDiff(current: string, patch: LintPatch, options: DiffRenderOptions = {}): DiffSegment[] {
  const ctxLines = options.contextLines ?? DEFAULT_CONTEXT_LINES
  if (!patch?.before) return [{ kind: 'orphan', current, patch }]

  const idx = current.indexOf(patch.before)
  if (idx === -1) return [{ kind: 'orphan', current, patch }]

  const beforeText = current.slice(0, idx)
  const afterText = current.slice(idx + patch.before.length)

  // Strip the newline that "belongs" to the patched line itself so the
  // surrounding context shows full lines of unrelated content. Without
  // this the trailing context block starts with an empty line (the
  // newline immediately after `before`) and one fewer real line of
  // context lands.
  const beforeContext = beforeText.endsWith('\n') ? beforeText.slice(0, -1) : beforeText
  const afterContext = afterText.startsWith('\n') ? afterText.slice(1) : afterText

  const segments: DiffSegment[] = []
  const leadingContext = trailingLines(beforeContext, ctxLines)
  if (leadingContext) segments.push({ kind: 'context', text: leadingContext })

  segments.push({ kind: 'removed', text: patch.before })
  segments.push({ kind: 'added', text: patch.after })

  const trailingContext = leadingLines(afterContext, ctxLines)
  if (trailingContext) segments.push({ kind: 'context', text: trailingContext })

  return segments
}

// Take the last N lines of `text` and ALWAYS append a trailing newline
// so the context block visually separates from the removed/added
// segment that follows it. Empty input returns empty.
function trailingLines(text: string, n: number): string {
  if (!text) return ''
  const lines = text.split('\n')
  // Trailing empty string from a trailing newline shouldn't count as a
  // displayed line — drop it before slicing.
  const trimmed = lines[lines.length - 1] === '' ? lines.slice(0, -1) : lines
  if (trimmed.length === 0) return ''
  const tail = trimmed.slice(-n).join('\n')
  return `${tail}\n`
}

// Symmetric to trailingLines — take the first N lines, always appending
// a trailing newline.
function leadingLines(text: string, n: number): string {
  if (!text) return ''
  const lines = text.split('\n')
  const trimmed = lines[lines.length - 1] === '' ? lines.slice(0, -1) : lines
  if (trimmed.length === 0) return ''
  const head = trimmed.slice(0, n).join('\n')
  return `${head}\n`
}
